import {
  BrandDefenseFilled,
  BrandSellInFilled,
  CategoryReviewFilled,
  DiagnosingGrowthFilled,
  formatData,
  IncrementalityFilled,
  WhosMyShopperFilled,
} from '@utilities';
import {
  COLOR_BANTAMWEIGHT,
  COLOR_DEEPTEAL,
  COLOR_NUMERATOR_GREEN,
  COLOR_SAPPHIRE,
  COLOR_SCALE_0,
  COLOR_WELTERWEIGHT,
} from '@utilities/helpers/colors';
import { transformColumn } from '@api/narratives/utilities/helpers';
import IndexBar from '../../../pages/Report/components/Tab/components/Visualizations/components/IndexBar';
import { narrativeStatus } from '@helpers/narratives';
import {
  HELP_CENTER_BASE_URL,
  HELP_CENTER_NARRATIVE_URLS,
} from '../../../pages/Narratives/utilities/constants';

/**
 * Returns the default structure for topic data.
 * This includes default initial error state, visibility, loading state, and title.
 *
 * @function getDefaultTopicData
 * @param {Object} topic
 * @returns {Object} - Initial topic data structure
 */
export const getDefaultTopicData = (topic) => {
  return {
    error: false,
    isHidden: topic.isHidden,
    loading: true,
    tags: topic.tags,
    title: topic.title,
  };
};

/**
 * Maps data variant to specific colors
 * @param {string} variant - The data variant ('primary', 'secondary', 'other')
 * @returns {string} - The corresponding color hex code
 */
export const getColorByVariant = (variant) => {
  const colorMap = {
    other: COLOR_SAPPHIRE,
    primary: COLOR_NUMERATOR_GREEN,
    secondary: COLOR_DEEPTEAL,
  };

  return colorMap[variant?.toLowerCase()] || COLOR_SCALE_0;
};

/**
 * Transforms the provided column metadata into react-table column definitions.
 * Adds a custom `Cell` renderer for index columns so we can append an `IndexBar` visualization.
 *
 * @param {Object[]} columnsData - Raw column definitions returned from the API.
 * @returns {Object[]} - Array of react-table column config objects.
 */
export const getTableColumns = (columnsData) => {
  const columns = columnsData.map((col) => {
    const column = transformColumn(col);
    const hasIndexBar = column.indexCol;

    return {
      ...column,
      // eslint-disable-next-line react/prop-types
      Cell: ({ getValue }) => {
        const value = formatData({
          format: column.format,
          value: getValue() || 'NAN_SENTINEL',
        });

        if (!hasIndexBar) return value;

        return (
          <span
            style={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            {value}
            <IndexBar value={getValue()} />
          </span>
        );
      },
      width: hasIndexBar ? 200 : column.width,
    };
  });

  return columns;
};

/**
 * Returns the appropriate loader header copy for the Narrative Flow status.
 *
 * @param {string} status - Current narrative flow status.
 * @returns {string} - Header copy for the loader component.
 */
export const loaderHeader = (status) => {
  switch (status) {
    case narrativeStatus.QUEUED:
      return 'In Queue';
    case narrativeStatus.SETUP:
      return 'Setup';
    default:
      return '';
  }
};

/**
 * Returns the message lines for the loader component based on the flow status.
 *
 * @param {Object} flow - Narrative flow object that contains a `status` field.
 * @returns {string[]} - Array of strings representing message lines.
 */
export const loaderMessage = (flow) => {
  if (!flow) {
    return [''];
  }

  switch (flow?.status) {
    case narrativeStatus.SETUP:
      return ['Validating Sample'];
    default:
      return [''];
  }
};

/**
 * Returns the icon component based on the Narrative ID
 * @param {string} narrativeId - The ID of the Narrative topic
 * @returns {React.Component|null} - The corresponding icon component or null if not found
 */
export const getIconByNarrativeId = (narrativeId) => {
  const iconMap = {
    'brand-defense': <BrandDefenseFilled />,
    'brand-sell-in': <BrandSellInFilled />,
    'category-review': <CategoryReviewFilled />,
    'diagnosing-growth': <DiagnosingGrowthFilled />,
    incrementality: <IncrementalityFilled />,
    'who-is-my-shopper': <WhosMyShopperFilled />,
  };

  return iconMap[narrativeId] || null;
};

/**
 * Normalizes visualization datasets into a structure expected by the PowerPoint export utility.
 * Supports bar/column charts, scatter plots, tables, and tree-based datasets.
 *
 * @param {Object} dataset - Visualization dataset describing a chart/table/tree.
 * @returns {Object} - PPT-friendly dataset shape for the provided visualization type.
 */
export const getPptStructuredData = (dataset) => {
  switch (dataset?.type) {
    case 'bar-chart':
    case 'bar-chart-two-labels':
    case 'column-chart':
    case 'bar-stack-chart':
      return {
        barDir: ['bar-chart', 'bar-chart-two-labels', 'bar-stack-chart'].includes(dataset.type)
          ? 'bar'
          : 'col',
        barGrouping: dataset.type === 'bar-stack-chart' ? 'percentStacked' : null,
        format: dataset?.yAxis?.labels?.format,
        series: dataset?.series,
        type: dataset?.type,
        xAxis: dataset?.xAxis,
      };
    case 'scatter-chart':
      return {
        formatX: dataset?.xAxis?.labels?.format,
        formatY: dataset?.yAxis?.labels?.format,
        nameX: dataset.xAxis?.title?.text || '',
        nameY: dataset?.yAxis?.title?.text || '',
        series: dataset?.series,
        type: dataset?.type,
        xAxis: dataset?.xAxis,
      };
    case 'table':
      return {
        columns: dataset?.columns,
        data: dataset?.data,
        type: dataset?.type,
      };
    case 'brand-tree-v2':
      return {
        type: 'brand-tree-v2',
      };
    default:
      // Only suitable for highchart components
      return {
        type: 'chart-image',
      };
  }
};

/**
 * Returns the Help Center article URL associated with a Narrative topic.
 * Falls back to the Help Center homepage if the topic does not have a dedicated article.
 *
 * @param {string} narrativeId - Narrative identifier used as the lookup key.
 * @returns {string} - Absolute URL to the Help Center article.
 */
export const getHelpArticleURLByNarrativeId = (narrativeId) => {
  return HELP_CENTER_NARRATIVE_URLS[narrativeId] || HELP_CENTER_BASE_URL;
};

// Temporary preview data for visualization preview cards
const PREVIEW_BAR_CATEGORIES = [
  'Retailer A',
  'Retailer B',
  'Retailer C',
  'Retailer D',
  'Retailer E',
  'Retailer F',
];
const PREVIEW_BAR_PRIMARY_VALUES = [92, 82, 63, 48, 36, 22];
const PREVIEW_BAR_SECONDARY_VALUES = [78, 66, 50, 42, 30, 18];
const PREVIEW_BAR_TWO_LABEL_DELTAS = [1.1, -0.6, 0.4, -1.0, 0.3, 0.8];

const PREVIEW_STACK_CATEGORIES = ['Product A', 'Product B', 'Product C', 'Product D', 'Product E'];

const PREVIEW_TABLE_ROWS = [
  {
    definition: 'Total spend expected this quarter',
    metric: 'Projected Sales',
  },
  {
    definition: 'Avg. spend per household',
    metric: 'Buy Rate',
  },
  {
    definition: 'Trips per household',
    metric: 'Purchase Frequency',
  },
  {
    definition: 'Units purchased each visit',
    metric: 'Units per Trip',
  },
];

const PREVIEW_BRAND_TREE_LEVELS = [
  [
    {
      description: 'Total spend on Ready To Drink Coffee at Any Store',
      metrics: [
        ['Projected Sales', '-'],
        ['Percent Change vs. Year Ago', '-'],
      ],
      title: 'Projected Sales',
    },
  ],
  [
    {
      description: 'Households buying the category at Any Store',
      metrics: [
        ['Projected Households', '-'],
        ['Percent Change vs. Year Ago', '-'],
      ],
      title: 'Projected Households',
    },
    {
      description: 'Average spend per household at Any Store',
      metrics: [
        ['Buy Rate', '-'],
        ['Percent Change vs. Year Ago', '-'],
      ],
      title: 'Buy Rate',
    },
  ],
  [
    {
      description: 'Trips per household to buy the category',
      metrics: [
        ['Purchase Frequency', '-'],
        ['Percent Change vs. Year Ago', '-'],
      ],
      title: 'Purchase Frequency',
    },
    {
      description: 'Total spend per trip for the category',
      metrics: [
        ['Spend per Trip', '-'],
        ['Percent Change vs. Year Ago', '-'],
      ],
      title: 'Spend per Trip',
    },
  ],
  [
    {
      description: 'Average spend per unit purchased of Coca-Cola Beverages at FMCG',
      metrics: [
        ['Spend per Unit', '-'],
        ['Percent Change vs. Year Ago', '-'],
      ],
      title: 'Spend per Unit',
    },
    {
      description: 'Average number of Coca-Cola Beverages units purchased per trip at FMCG',
      metrics: [
        ['Units per Trip', '-'],
        ['Percent Change vs. Year Ago', '-'],
      ],
      title: 'Units per Trip',
    },
  ],
];

const PREVIEW_EBSOVA_TREE_LEVELS = [
  [
    {
      description: 'Change in Projected Sales for product',
      metrics: [
        ['% Change for product vs Year Ago', '-'],
        ['Absolute Change for product vs Year Ago', '-'],
      ],
      title: 'Total Change in Projected Sales',
    },
  ],
  [
    {
      description: 'Change in Projected Sales due to shoppers entering or leaving category',
      metrics: [
        ['Category churn contribution to % change for product', '-'],
        ['Category churn contribution to absolute change for product', '-'],
      ],
      title: 'Due to Category Churn',
    },
    {
      description: 'Change in Projected Sales due to shoppers spending more or less on category',
      metrics: [
        ['Category expansion/contraction contribution to % change for product', '-'],
        ['Category expansion/contraction contribution to absolute change for product', '-'],
      ],
      title: 'Due to Category Expansion/Contraction',
    },
    {
      description:
        'Change in Projected Sales due to shoppers shifting spend from/to competitive brands',
      metrics: [
        ['Brand shifting contribution to % change for product', '-'],
        ['Brand shifting contribution to absolute change for product', '-'],
      ],
      title: 'Due to Brand Shifting',
    },
  ],
];

const PREVIEW_SCATTER_POINTS = [
  {
    colorType: 'primary',
    name: 'Retailer Focus',
    x: 32,
    y: 58,
  },
  {
    colorType: 'secondary',
    name: 'Category Leader',
    x: 24,
    y: 44,
  },
  {
    colorType: 'other',
    name: 'Emerging Player',
    x: 15,
    y: 36,
  },
];

const buildPreviewBarSeries = () => [
  {
    color: COLOR_BANTAMWEIGHT,
    data: [...PREVIEW_BAR_PRIMARY_VALUES],
    name: '% of Spend 1YA',
  },
  {
    color: COLOR_WELTERWEIGHT,
    data: [...PREVIEW_BAR_SECONDARY_VALUES],
    name: '% of Spend',
  },
];

const buildPreviewTwoLabelSeries = () => [
  {
    color: COLOR_BANTAMWEIGHT,
    data: PREVIEW_BAR_CATEGORIES.map((category, index) => {
      const change = PREVIEW_BAR_TWO_LABEL_DELTAS[index % PREVIEW_BAR_TWO_LABEL_DELTAS.length];
      return {
        name: category,
        y: PREVIEW_BAR_SECONDARY_VALUES[index],
        y2: change,
        yDelta: change >= 0 ? 1 : -1,
      };
    }),
    dataLabels: [
      {
        format: '0.0%',
      },
      {
        format: '0.0',
      },
    ],
    name: '% of Spend',
  },
];

const buildPreviewStackSeries = () => [
  {
    color: COLOR_BANTAMWEIGHT,
    data: [45, 52, 46, 58, 60],
    name: 'Store Share',
  },
  {
    color: COLOR_WELTERWEIGHT,
    data: [55, 48, 54, 42, 40],
    name: 'Other Retailers',
  },
];

const buildPreviewTableColumns = () => [
  {
    accessor: 'metric',
    Header: 'Metric',
    width: 160,
  },
  {
    accessor: 'current',
    Header: 'Current',
  },
  {
    accessor: 'change',
    Header: 'Change',
  },
  {
    accessor: 'index',
    Header: 'Index',
  },
];

const buildPreviewTableData = () =>
  PREVIEW_TABLE_ROWS.map(({ definition, metric }) => ({
    change: null,
    current: null,
    definition,
    metric,
  }));

const buildPreviewTreeNodes = (treeLevels) => {
  let nodeIndex = 0;
  return treeLevels.flatMap((row, rowIndex) =>
    row.map(({ description, metrics, title }) => {
      nodeIndex += 1;
      const node = {
        color: COLOR_BANTAMWEIGHT,
        description,
        header: title,
        inline: true,
        node_id: `node_${nodeIndex}`,
        node_row: rowIndex + 1,
        subdescription: '',
      };

      metrics.forEach((metricRow, metricRowIndex) => {
        node[`r${metricRowIndex + 1}c1_description`] = metricRow[0];
        node[`r${metricRowIndex + 1}c2_value`] = metricRow[1];
      });

      return node;
    })
  );
};

const buildPreviewScatterSeries = () => [
  {
    data: PREVIEW_SCATTER_POINTS.map((point) => ({ ...point })),
    dataLabels: {
      enabled: true,
    },
    name: 'Retailers',
  },
];

const asPreviewDataset = (dataset) => (dataset ? { ...dataset, __isPreview: true } : null);

const maskNonFirstColumnValue = (value) => {
  if (value == null) return null;
  if (Array.isArray(value)) return value.map(() => null);
  if (typeof value === 'object') {
    return Object.keys(value).reduce(
      (acc, key) => {
        acc[key] = maskNonFirstColumnValue(value[key]);
        return acc;
      },
      Array.isArray(value) ? [] : {}
    );
  }
  if (typeof value === 'number') return null;
  return '';
};

const sanitizeTableDataset = (dataset) => {
  const firstColumnAccessor = dataset?.columns?.[0]?.accessor;
  const sanitizedData = Array.isArray(dataset?.data)
    ? dataset.data.map((row) => {
        if (!row || typeof row !== 'object') return row;
        return Object.keys(row).reduce((acc, key) => {
          if (firstColumnAccessor && key === firstColumnAccessor) {
            acc[key] = row[key];
          } else {
            acc[key] = maskNonFirstColumnValue(row[key]);
          }
          return acc;
        }, {});
      })
    : [];

  return {
    ...dataset,
    data: sanitizedData,
  };
};

const sanitizeChartPoint = (point) => {
  if (point == null) return null;
  if (typeof point === 'number') return null;
  if (Array.isArray(point)) return point.map(() => null);
  if (typeof point === 'object') {
    const sanitizedPoint = { ...point };
    if ('y' in sanitizedPoint) sanitizedPoint.y = null;
    if ('y2' in sanitizedPoint) sanitizedPoint.y2 = null;
    if ('value' in sanitizedPoint) sanitizedPoint.value = null;
    if ('x' in sanitizedPoint)
      sanitizedPoint.x = typeof sanitizedPoint.x === 'number' ? null : sanitizedPoint.x;
    return sanitizedPoint;
  }
  return null;
};

/**
 * Removes metric values from chart series so draft datasets do not leak real data.
 *
 * @param {Object[]} [series=[]] - Highcharts series data.
 * @returns {Object[]} - Series with sanitized data points.
 */
const sanitizeSeries = (series = []) =>
  series.map((seriesItem) => ({
    ...seriesItem,
    data: Array.isArray(seriesItem.data)
      ? seriesItem.data.map((point) => sanitizeChartPoint(point))
      : [],
  }));

/**
 * Clears metric cells for tree datasets so we only surface structural information in drafts.
 *
 * @param {Object[]} [nodes=[]] - Tree node definitions.
 * @returns {Object[]} - Nodes containing only descriptive text.
 */
const sanitizeTreeNodes = (nodes = []) =>
  nodes.map((node) => {
    const sanitizedNode = { ...node };
    Object.keys(sanitizedNode).forEach((key) => {
      if (/c\d+_value$/i.test(key) || key.endsWith('_value') || key.endsWith('_value_savings')) {
        sanitizedNode[key] = '';
      }
    });
    return sanitizedNode;
  });

/**
 * Sanitizes any dataset that will be displayed in Narrative Flow drafts by stripping metric values.
 *
 * @param {Object} dataset - Visualization dataset sourced from the API.
 * @returns {Object} - Dataset containing only safe preview-friendly values.
 */
export const sanitizeDraftDataset = (dataset) => {
  if (!dataset || typeof dataset !== 'object') return dataset;
  switch (dataset.type) {
    case 'table':
      return sanitizeTableDataset(dataset);
    case 'brand-tree-v2':
    case 'ebsova-tree':
      return {
        ...dataset,
        nodes: sanitizeTreeNodes(dataset.nodes),
      };
    default:
      return {
        ...dataset,
        series: sanitizeSeries(dataset.series),
      };
  }
};

/**
 * Generates preview datasets for visualization cards when real data is not yet ready.
 *
 * @param {string} type - Visualization type (e.g., 'bar-chart', 'table').
 * @returns {Object|null} - Preview dataset decorated with the `__isPreview` flag or null if unsupported.
 */
export const getPreviewDataset = (type) => {
  if (!type) return null;
  switch (type) {
    case 'bar-chart':
      return asPreviewDataset({
        series: buildPreviewBarSeries(),
        type,
        xAxis: {
          categories: [...PREVIEW_BAR_CATEGORIES],
        },
        yAxis: {
          labels: {
            format: '0.0%',
          },
        },
      });
    case 'bar-chart-two-labels':
      return asPreviewDataset({
        series: buildPreviewTwoLabelSeries(),
        type,
        xAxis: {
          categories: [...PREVIEW_BAR_CATEGORIES],
        },
        yAxis: {
          labels: {
            format: '0.0%',
          },
        },
      });
    case 'bar-stack-chart':
      return asPreviewDataset({
        series: buildPreviewStackSeries(),
        type,
        xAxis: {
          categories: [...PREVIEW_STACK_CATEGORIES],
        },
        yAxis: {
          labels: {
            format: '0%',
          },
        },
      });
    case 'column-chart':
      return asPreviewDataset({
        series: buildPreviewBarSeries(),
        type,
        xAxis: {
          categories: [...PREVIEW_BAR_CATEGORIES],
        },
        yAxis: {
          labels: {
            format: '0.0%',
          },
        },
      });
    case 'scatter-chart':
      return asPreviewDataset({
        series: buildPreviewScatterSeries(),
        type,
        xAxis: {
          labels: {
            format: '0%',
          },
          max: 60,
          plotLines: [
            {
              color: COLOR_WELTERWEIGHT,
              value: 28,
            },
          ],
          title: {
            text: 'Household Penetration',
          },
        },
        yAxis: {
          labels: {
            format: '$0,0.0',
          },
          max: 80,
          plotLines: [
            {
              color: COLOR_WELTERWEIGHT,
              value: 45,
            },
          ],
          title: {
            text: 'Trips per Buyer',
          },
        },
      });
    case 'table':
      return asPreviewDataset({
        columns: buildPreviewTableColumns(),
        data: buildPreviewTableData(),
        type,
      });
    case 'brand-tree-v2':
    case 'ebsova-tree':
      return asPreviewDataset({
        nodes: buildPreviewTreeNodes(
          type === 'brand-tree-v2' ? PREVIEW_BRAND_TREE_LEVELS : PREVIEW_EBSOVA_TREE_LEVELS
        ),
        type,
      });
    default:
      return null;
  }
};
